/**
 * Date utility functions for formatting and parsing dates
 */

// Vietnam timezone offset: UTC+7 (7 hours in milliseconds)
const VIETNAM_TIMEZONE_OFFSET = 7 * 60 * 60 * 1000;

/**
 * Convert a date string/object to Vietnam timezone (UTC+7)
 * Handles both UTC dates (with Z suffix) and dates without timezone info
 * @param {String|Date} dateInput - Date string or Date object
 * @returns {Date} - Date object adjusted to Vietnam timezone
 */
const convertToVietnamTime = (dateInput) => {
  let date;

  if (typeof dateInput === 'string') {
    // If date string doesn't have timezone info, treat it as UTC by adding 'Z'
    if (!dateInput.endsWith('Z') && !dateInput.includes('+') && !dateInput.includes('-', 10)) {
      date = new Date(dateInput + 'Z');
    } else {
      date = new Date(dateInput);
    }
  } else {
    date = dateInput;
  }

  // Get UTC timestamp and add Vietnam offset
  const utcTime = date.getTime();
  return new Date(utcTime + VIETNAM_TIMEZONE_OFFSET);
};

/**
 * Format date to YYYY-MM-DD format for HTML5 date input
 * @param {String|Date} dateString - Date string in any format or Date object
 * @returns {String} - Date in YYYY-MM-DD format
 */
export const formatDateForInput = (dateString) => {
  if (!dateString) return '';

  // If already in YYYY-MM-DD format, return as is
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // Try to parse the date
  const date = new Date(dateString);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return '';
  }

  // Format to YYYY-MM-DD
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Format date to display format (DD/MM/YYYY) in Vietnam timezone (UTC+7)
 * @param {String|Date} dateString - Date string in any format or Date object
 * @returns {String} - Date in DD/MM/YYYY format (Vietnam timezone)
 */
export const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';

  // Convert to Vietnam timezone (UTC+7)
  const vietnamDate = convertToVietnamTime(dateString);

  // Check if date is valid
  if (isNaN(vietnamDate.getTime())) {
    return '';
  }

  // Format to DD/MM/YYYY using UTC methods (since vietnamDate represents Vietnam time as UTC)
  const year = vietnamDate.getUTCFullYear();
  const month = String(vietnamDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(vietnamDate.getUTCDate()).padStart(2, '0');

  return `${day}/${month}/${year}`;
};

/**
 * Format date to display format based on culture in Vietnam timezone (UTC+7)
 * @param {String|Date} dateString - Date string in any format or Date object
 * @param {String} culture - Culture code ('vi' or 'en')
 * @returns {String} - Date formatted based on culture (DD/MM/YYYY for vi, MM/DD/YYYY for en) in Vietnam timezone
 */
export const formatDateWithCulture = (dateString, culture = 'vi') => {
  if (!dateString) return '';

  // Convert to Vietnam timezone (UTC+7)
  const vietnamDate = convertToVietnamTime(dateString);

  // Check if date is valid
  if (isNaN(vietnamDate.getTime())) {
    return '';
  }

  // Use UTC methods since vietnamDate represents Vietnam time as UTC
  const year = vietnamDate.getUTCFullYear();
  const month = String(vietnamDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(vietnamDate.getUTCDate()).padStart(2, '0');

  // Format based on culture
  if (culture === 'en') {
    // English format: MM/DD/YYYY
    return `${month}/${day}/${year}`;
  } else {
    // Vietnamese format: DD/MM/YYYY (default)
    return `${day}/${month}/${year}`;
  }
};

/**
 * Get current date in YYYY-MM-DD format
 * @returns {String} - Current date in YYYY-MM-DD format
 */
export const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Check if a date is valid
 * @param {String|Date} dateString - Date string in any format or Date object
 * @returns {Boolean} - True if date is valid
 */
export const isValidDate = (dateString) => {
  if (!dateString) return false;

  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

/**
 * Compare two dates
 * @param {String|Date} date1 - First date
 * @param {String|Date} date2 - Second date
 * @returns {Number} - -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export const compareDates = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  if (d1.getTime() < d2.getTime()) return -1;
  if (d1.getTime() > d2.getTime()) return 1;
  return 0;
};

/**
 * Format datetime to display format with time (DD/MM/YYYY HH:mm:ss) in Vietnam timezone (UTC+7)
 * @param {String|Date} dateString - Date string in any format or Date object
 * @returns {String} - DateTime in DD/MM/YYYY HH:mm:ss format (Vietnam timezone)
 */
export const formatDateTimeForDisplay = (dateString) => {
  if (!dateString) return '';

  // Convert to Vietnam timezone (UTC+7)
  const vietnamDate = convertToVietnamTime(dateString);

  // Check if date is valid
  if (isNaN(vietnamDate.getTime())) {
    return '';
  }

  // Format to DD/MM/YYYY HH:mm:ss using UTC methods
  const year = vietnamDate.getUTCFullYear();
  const month = String(vietnamDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(vietnamDate.getUTCDate()).padStart(2, '0');
  const hours = String(vietnamDate.getUTCHours()).padStart(2, '0');
  const minutes = String(vietnamDate.getUTCMinutes()).padStart(2, '0');
  const seconds = String(vietnamDate.getUTCSeconds()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

/**
 * Format datetime to display format based on culture with time in Vietnam timezone (UTC+7)
 * @param {String|Date} dateString - Date string in any format or Date object
 * @param {String} culture - Culture code ('vi' or 'en')
 * @returns {String} - DateTime formatted based on culture (DD/MM/YYYY HH:mm:ss for vi, MM/DD/YYYY HH:mm:ss for en) in Vietnam timezone
 */
export const formatDateTimeWithCulture = (dateString, culture = 'vi') => {
  if (!dateString) return '';

  // Convert to Vietnam timezone (UTC+7)
  const vietnamDate = convertToVietnamTime(dateString);

  // Check if date is valid
  if (isNaN(vietnamDate.getTime())) {
    return '';
  }

  // Use UTC methods since vietnamDate represents Vietnam time as UTC
  const year = vietnamDate.getUTCFullYear();
  const month = String(vietnamDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(vietnamDate.getUTCDate()).padStart(2, '0');
  const hours = String(vietnamDate.getUTCHours()).padStart(2, '0');
  const minutes = String(vietnamDate.getUTCMinutes()).padStart(2, '0');
  const seconds = String(vietnamDate.getUTCSeconds()).padStart(2, '0');

  const time = `${hours}:${minutes}:${seconds}`;

  // Format based on culture
  if (culture === 'en') {
    // English format: MM/DD/YYYY HH:mm:ss
    return `${month}/${day}/${year} ${time}`;
  } else {
    // Vietnamese format: DD/MM/YYYY HH:mm:ss (default)
    return `${day}/${month}/${year} ${time}`;
  }
};
